import React from 'react';
import Card from '@mui/material/Card';
import Icon from '@mui/material/Icon';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import Tooltip from '@mui/material/Tooltip';
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';

import EmptyStateCard from './EmptyStateCard';
import { trMoney } from '../../utils/accountingFormat';

function formatCategoryLabel(category) {
  switch (category) {
    case 'RENT':
      return 'Kira';
    case 'UTILITIES':
      return 'Fatura';
    case 'CLEANING':
      return 'Temizlik';
    case 'MARKETING':
      return 'Pazarlama';
    case 'EQUIPMENT':
      return 'Ekipman';
    case 'SHOPPING':
      return 'Market';
    case 'MAINTENANCE':
      return 'Bakım';
    case 'TAX':
      return 'Vergi';
    default:
      return 'Diğer';
  }
}

function ExpenseRow({ item, mode, onMarkPaid, onMoveToPending, onDelete, onDragStart, onDragEnd }) {
  const isPaid = item.status === 'PAID';
  const hasNote = Boolean(item.note && String(item.note).trim());
  const [open, setOpen] = React.useState(false);

  const handleToggleNote = () => {
    if (!hasNote) return;
    setOpen((prev) => !prev);
  };

  return (
    <Card
      draggable
      onDragStart={() => onDragStart?.(item)}
      onDragEnd={() => onDragEnd?.()}
      sx={{
        borderRadius: '14px',
        border: '1px solid rgba(0,0,0,0.08)',
        boxShadow: 'none',
        cursor: 'grab',
        overflow: 'hidden',
        '&:active': {
          cursor: 'grabbing',
        },
      }}
    >
      <MDBox
        sx={{
          p: 1.25,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          flexWrap: 'wrap',
        }}
      >
        <MDBox
          sx={{
            minWidth: 0,
            flex: 1,
            cursor: hasNote ? 'pointer' : 'default',
          }}
          onClick={handleToggleNote}
        >
          <MDBox sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
            <MDTypography variant="button" fontWeight="bold">
              {item.title || 'Gider'}
            </MDTypography>

            {hasNote && (
              <Icon
                fontSize="small"
                sx={{
                  fontSize: 18,
                  color: 'text.secondary',
                  transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 160ms ease',
                }}
              >
                expand_more
              </Icon>
            )}
          </MDBox>

          <MDTypography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
            {formatCategoryLabel(item.category)}
          </MDTypography>
        </MDBox>

        <MDBox
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            flexWrap: 'wrap',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <Chip
            size="small"
            label={trMoney(item.amountTry ?? 0)}
            sx={{ borderRadius: '999px', fontWeight: 800 }}
          />

          {isPaid ? (
            <Chip
              size="small"
              color="success"
              label="Ödendi"
              sx={{ borderRadius: '999px', fontWeight: 700 }}
            />
          ) : (
            <Chip
              size="small"
              color="warning"
              label="Bekliyor"
              sx={{ borderRadius: '999px', fontWeight: 700 }}
            />
          )}

          {mode === 'PENDING' && onMarkPaid && (
            <Tooltip title="Ödendi yap">
              <IconButton size="small" onClick={() => onMarkPaid(item)}>
                <Icon fontSize="small">east</Icon>
              </IconButton>
            </Tooltip>
          )}

          {mode === 'PAID' && onMoveToPending && (
            <Tooltip title="Bekleyene al">
              <IconButton size="small" onClick={() => onMoveToPending(item)}>
                <Icon fontSize="small">west</Icon>
              </IconButton>
            </Tooltip>
          )}

          {onDelete && (
            <Tooltip title="Sil">
              <IconButton size="small" color="error" onClick={() => onDelete(item)}>
                <Icon fontSize="small">delete</Icon>
              </IconButton>
            </Tooltip>
          )}
        </MDBox>
      </MDBox>

      {hasNote && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <Divider />
          <MDBox sx={{ px: 1.25, py: 1.1, backgroundColor: 'rgba(0,0,0,0.015)' }}>
            <MDTypography
              variant="caption"
              fontWeight="bold"
              sx={{ display: 'block', mb: 0.5, color: 'text.secondary' }}
            >
              Not
            </MDTypography>
            <MDTypography variant="button" sx={{ color: 'text.primary', whiteSpace: 'pre-wrap' }}>
              {item.note}
            </MDTypography>
          </MDBox>
        </Collapse>
      )}
    </Card>
  );
}

function ExpensesList({
  rows = [],
  mode,
  onMarkPaid,
  onMoveToPending,
  onDelete,
  onDragStart,
  onDragEnd,
}) {
  const sortedRows = React.useMemo(() => {
    return [...rows].sort((a, b) => {
      const da = new Date(a.paidDate || a.dueDate || a.createdAt || 0);
      const db = new Date(b.paidDate || b.dueDate || b.createdAt || 0);
      return db - da;
    });
  }, [rows]);

  if (!sortedRows.length) {
    return (
      <EmptyStateCard icon="receipt_long" title="Gider bulunamadı" desc="Henüz gider eklenmemiş." />
    );
  }

  return (
    <Grid container spacing={1.25}>
      {sortedRows.map((item) => (
        <Grid item xs={12} key={item.id}>
          <ExpenseRow
            item={item}
            mode={mode}
            onMarkPaid={onMarkPaid}
            onMoveToPending={onMoveToPending}
            onDelete={onDelete}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
          />
        </Grid>
      ))}
    </Grid>
  );
}

export default React.memo(ExpensesList);
